import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent, Typography, Chip, Box, Avatar, Stack, Tooltip } from '@mui/material';
import { Notifications, CheckCircle, Close, ErrorOutline, WarningAmber, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSystemMessagesForNotificationQuery, useSystemMessageNotificationAcknowledgeMutation, EnumSystemMessageAudience, EnumSystemMessageMessageType, SystemMessagesForNotificationQuery } from '../Queries';
import { useRecoilValue } from 'recoil';
import { CurrentUserState, CurrentUserIsAdmin } from '../atoms/CurrentUser';
import dayjs from 'dayjs';

export const SystemMessageNotification = () => {
   const navigate = useNavigate();
   const currentUser = useRecoilValue(CurrentUserState);
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const [dialogOpen, setDialogOpen] = useState(false);
   const isPollingStoppedRef = useRef(false);

   const { data, refetch, startPolling, stopPolling } = useSystemMessagesForNotificationQuery({
      variables: {
         limit: 100,
         filter: {
            acknowledgedTs: null // Only unacknowledged messages
         }
      },
      fetchPolicy: 'cache-and-network',
      pollInterval: 30000, // Poll every 30 seconds for new messages
      errorPolicy: 'all', // Continue showing data even if there are errors
      onError: (error) => {
         // Stop polling on any error to prevent spam (network issues, auth errors, etc.)
         stopPolling();
         isPollingStoppedRef.current = true;
         console.warn('SystemMessageNotification: Error detected, stopping polling:', error.message || error);
      }
   });

   const [acknowledgeMessage] = useSystemMessageNotificationAcknowledgeMutation();

   // Restart polling if we receive valid data after authentication was restored
   useEffect(() => {
      if (data && isPollingStoppedRef.current) {
         console.log('SystemMessageNotification: Authentication restored, restarting polling');
         startPolling(30000);
         isPollingStoppedRef.current = false;
      }
   }, [data, startPolling]);

   type NotificationSystemMessage = SystemMessagesForNotificationQuery["SystemMessages"][number];
   type NotificationSystemMessageEntry = NonNullable<NotificationSystemMessage["entries"][number]>;

   const getEntries = (message: NotificationSystemMessage): NotificationSystemMessageEntry[] => (
      message.entries.filter((entry): entry is NotificationSystemMessageEntry => Boolean(entry))
   );

   // Filter messages client-side based on user's audience
   const isMessageRelevantToUser = (message: NotificationSystemMessage) => {
      // Check if message is for administrator and user is admin
      if (message.audience === EnumSystemMessageAudience.ADMIN && isAdmin) {
         return true;
      }

      // Check if message is for brandhouse and user has brandhouse access
      if (message.audience === EnumSystemMessageAudience.BRAND && currentUser?.brandHouseId) {
         // If audienceBrandId is specified, check if it matches user's brandhouse
         return !message.audienceBrandId || message.audienceBrandId === currentUser.brandHouseId;
      }

      // System admin messages are only for system administrators
      if (message.audience === EnumSystemMessageAudience.SYSADM && isAdmin) {
         return true;
      }

      return false;
   };

   const relevantMessages = data?.SystemMessages?.filter(isMessageRelevantToUser) || [];
   const unacknowledgedCount = relevantMessages.length;

   const handleClick = () => {
      setDialogOpen(true);
   };

   const handleAcknowledge = async (messageId: string) => {
      try {
         await acknowledgeMessage({
            variables: {
               filter: { _id: messageId },
               record: { acknowledgedTs: new Date() }
            }
         });
         refetch(); // Refresh the data after acknowledging
      } catch (error) {
         console.error('Failed to acknowledge message:', error);
      }
   };

   const handleSeeMore = () => {
      setDialogOpen(false);
      navigate('/admin/systemmessages');
   };

   const getMessageTypeColor = (messageType: EnumSystemMessageMessageType) => {
      switch (messageType) {
         case EnumSystemMessageMessageType.CRITICAL:
            return 'error';
         case EnumSystemMessageMessageType.WARNING:
            return 'warning';
         case EnumSystemMessageMessageType.INFORMATION:
         default:
            return 'info';
      }
   };

   const getMessageTypeBg = (messageType: EnumSystemMessageMessageType) => `${getMessageTypeColor(messageType)}.main`;

   const getMessageTypeIcon = (messageType: EnumSystemMessageMessageType) => {
      switch (messageType) {
         case EnumSystemMessageMessageType.CRITICAL:
            return <ErrorOutline fontSize="small" />;
         case EnumSystemMessageMessageType.WARNING:
            return <WarningAmber fontSize="small" />;
         case EnumSystemMessageMessageType.INFORMATION:
         default:
            return <InfoOutlined fontSize="small" />;
      }
   };

   if (unacknowledgedCount === 0) {
      return null;
   }

   return (
      <>
         <IconButton
            color="inherit"
            onClick={handleClick}
            title={`${unacknowledgedCount} unacknowledged system message${unacknowledgedCount !== 1 ? 's' : ''}`}
            size="large"
         >
            <Badge badgeContent={unacknowledgedCount} color="error">
               <Notifications />
            </Badge>
         </IconButton>

         <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
         >
            <DialogTitle sx={{ bgcolor: '#f0f0f0' }}>
               <Box display="flex" justifyContent="space-between" alignItems="center">
                  System Messages
                  <IconButton onClick={() => setDialogOpen(false)} size="small">
                     <Close />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: '#f0f0f0' }}>
               <Box display="flex" flexDirection="column" gap={2}>
                  {relevantMessages.map((message) => (
                     <Card key={message._id} variant="outlined">
                        <CardContent>
                           <Stack direction="row" spacing={2} alignItems="flex-start">
                              <Tooltip title={message.messageType} placement="top">
                                 <Avatar sx={{ bgcolor: getMessageTypeBg(message.messageType), width: 32, height: 32 }}>
                                    {getMessageTypeIcon(message.messageType)}
                                 </Avatar>
                              </Tooltip>
                              <Box flexGrow={1} minWidth={0}>
                                 <Typography variant="subtitle2" component="div">
                                    {message.subject}
                                 </Typography>
                                 <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                    {getEntries(message).map((entry, index) => (
                                       <Box key={`${message._id}-${entry.timestamp}-${index}`}>
                                          <Box
                                             component="pre"
                                             sx={{
                                                m: 0,
                                                fontFamily: 'inherit',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                             }}
                                          >
                                             {entry.text}
                                          </Box>
                                          <Typography variant="caption" color="text.secondary">
                                             {dayjs(entry.timestamp).format('MMM D, YYYY HH:mm')}
                                          </Typography>
                                       </Box>
                                    ))}
                                 </Box>
                                 <Typography variant="body2" color="text.secondary">
                                    Last activity {dayjs(message.lastActivityTs).format('MMM D, YYYY HH:mm')}
                                 </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" justifyContent="flex-end" minWidth={160}>
                                 {message.acknowledgedTs ? (
                                    <Chip
                                       icon={<CheckCircle />}
                                       label={`Acknowledged ${dayjs(message.acknowledgedTs).format('MMM D')}`}
                                       color="success"
                                       size="small"
                                    />
                                 ) : (
                                    <Button
                                       variant="contained"
                                       color={getMessageTypeColor(message.messageType)}
                                       size="small"
                                       onClick={() => handleAcknowledge(message._id)}
                                       startIcon={<CheckCircle />}
                                    >
                                       Acknowledge
                                    </Button>
                                 )}
                              </Box>
                           </Stack>
                        </CardContent>
                     </Card>
                  ))}
               </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#f0f0f0' }}>
               {isAdmin && (
                  <Button variant="outlined" onClick={handleSeeMore}>
                     See more
                  </Button>
               )}
               <Button variant="contained" onClick={() => setDialogOpen(false)}>
                  Close
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};
